// Form inicia sesion que solicita usuario y contrasena y monta el panel principal. claude.
import React, { useState, useEffect, createContext, useContext } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as LucideIcons from 'lucide-react';
import AdminLayout from './components/admin/AdminLayout';
import SqlexecPanel from './components/sqlexec/SqlexecPanel';
import { secureFetch } from './utils/authInterceptor'; // 🔥 INTERCEPTOR GLOBAL
import SystemManifesto from './components/SystemManifesto';
import { useSessionTimer, verifyTokenNow } from './utils/sessionTimer'; // 🔥 TIMER ESTILO VFP9 + BANCARIO
import { SisConfigProvider, useSisConfig } from './contexts/SisConfigUi';
import { cacheClean } from './utils/errFrontend';
import { useVersion } from './hooks/useVersion';

// ===== FUNCIÓN PARA ICONOS DINÁMICOS =====
const getDynamicIcon = (iconName, size = 20, className = "") => {
  if (!iconName) return <LucideIcons.Circle size={size} className={className} />;
  
  const componentName = iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
    
  const IconComponent = LucideIcons[componentName];
  
  return IconComponent ? 
    <IconComponent size={size} className={className} /> : 
    <LucideIcons.Circle size={size} className={className} />;
};

// ===== CONTEXT PARA TEMA OSCURO =====
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('mapasac_theme');
    return saved ? JSON.parse(saved) : false;
  });

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('mapasac_theme', JSON.stringify(newTheme));
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// ===== COMPONENTE LOGIN =====
const LoginForm = ({ onLoginSuccess }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const version = useVersion();
  const [loginLoading, setLoginLoading] = useState(false);
  const [usuario, setUsuario] = useState(() => {
    return localStorage.getItem('mapasac_last_user') || '';
  });
  const [password, setPassword] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('checking');

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await fetch('/api/health'); //original fetch
        setConnectionStatus(response.ok ? 'connected' : 'error');
      } catch (error) {
        setConnectionStatus('error');
      }
    };

    // ← Inmediato — backend ya está corriendo
    checkConnection();
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);

  }, []);

  const handleLogin = async () => {
    if (!usuario.trim() || !password.trim()) {
      setPassword('');
      return;
    }

    setLoginLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login: usuario.trim(),
          password: password.trim(),
          client_info: {
            user_agent: navigator.userAgent,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        const sessionData = {
          user: data.data.user,
          empresa: data.data.empresa,
          auth: data.data.auth,
          rbac: data.data.rbac,
          database_info: data.data.database_info,
          session: data.data.session,
          ui_config: data.data.ui_config
        };

        sessionStorage.setItem('mapasac_access_token', data.data.auth.access_token);
        sessionStorage.setItem('mapasac_refresh_token', data.data.auth.refresh_token);
        sessionStorage.setItem('mapasac_session', JSON.stringify(sessionData));
        localStorage.setItem('mapasac_last_user', usuario.trim());


        ///console.log('🔍 SESSION DATA:', JSON.stringify(sessionData, null, 2)); goooooodddddd

        
        toast.success(`¡Bienvenido ${data.data.user.login}!`);
        onLoginSuccess(sessionData);        
      } else {
        setPassword('');
        toast.error(data.error?.message);
      }
    } catch (error) {
      console.error('Error en login:', error);
      setPassword('');
      toast.error('Error de conexión');
    } finally {
      setLoginLoading(false);
    }
  };

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected': return getDynamicIcon('wifi', 16, 'text-green-500');
      case 'error': return getDynamicIcon('wifi-off', 16, 'text-red-500');
      default: return getDynamicIcon('loader-2', 16, 'animate-spin text-yellow-500');
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${
      isDarkMode 
        ? 'from-gray-900 via-blue-900 to-indigo-900' 
        : 'from-blue-600 via-blue-700 to-indigo-800'
    } flex items-center justify-center p-4`}>
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 p-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all"
      >
        {isDarkMode ? 
          getDynamicIcon('sun', 24, 'text-yellow-400') : 
          getDynamicIcon('moon', 24, 'text-blue-200')
        }
      </button>
      
      <div className="w-full max-w-md">
        <div className="text-center mb-4">
          <div className="flex items-center justify-center mb-4">
            <div className={`p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-full shadow-lg`}>
              {getDynamicIcon('shield', 32, 'text-blue-600')}
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-0">MAPASAC</h1>
          <p className={`${isDarkMode ? 'text-gray-300' : 'text-blue-100'}`}>Sistema de Gestión Empresarial</p>
          <div className="flex items-center justify-center mt-3 space-x-2">
            {getConnectionIcon()}
            <span className={`text-xs ${
              connectionStatus === 'connected' ? 'text-green-300' :
              connectionStatus === 'error' ? 'text-red-300' : 'text-yellow-300'
            }`}>
              {connectionStatus === 'connected' ? 'Conectado al servidor' :
               connectionStatus === 'error' ? 'Error de conexión' : 'Verificando conexión...'}
            </span>
          </div>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl p-9`}>
          <div className="text-center mb-4">
            {getDynamicIcon('user', 48, `mx-auto ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} mb-0`)}
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Iniciar Sesión</h2>
          </div>
          <div className="mb-4">
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-0`}>
              Nombre de Usuario
            </label>
            <input
              type="text"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('password-input')?.focus();
                }
              }}
              className={`w-full p-2 rounded-lg border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all`}
              placeholder="Ingresa tu usuario"
              disabled={loginLoading}
              autoComplete="username"
            />
          </div>
          <div className="mb-10">
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-0`}>
              Contraseña
            </label>
            <input
              type="password"
              id="password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLogin();
                }
              }}
              className={`w-full p-2 rounded-lg border ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all`}
              placeholder="Ingresa tu contraseña"
              disabled={loginLoading}
              autoComplete="current-password"
            />
          </div>
          <button
            onClick={handleLogin}
            disabled={loginLoading || !usuario.trim() || !password.trim() || connectionStatus === 'error'}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all transform hover:scale-105 active:scale-95"
          >
            {loginLoading ? (
              getDynamicIcon('loader-2', 20, 'animate-spin mr-2')
            ) : (
              getDynamicIcon('check-circle', 20, 'mr-2')
            )}
            {loginLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </div>
        <div className={`text-center mt-6 ${isDarkMode ? 'text-gray-300' : 'text-blue-100'}`}>
          <div className="flex items-center justify-center mb-0">
            {getDynamicIcon('globe', 16, 'mr-1')}
            <span className="text-sm">Sistema RBAC Multi-tenant</span>
          </div>
          <p className="text-xs">
            💪 Powered By Mapasac ©1996-{new Date().getFullYear()} All Rights Reserved{version ? ` · Ver. ${version}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

// ===== COMPONENTE DASHBOARD 100% DINÁMICO =====
const DashboardComponent = ({ sessionData, onLogout }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { isDesarrollo } = useSisConfig(); //, reloadUIConfig } = useSisConfig();

  const [activeModule, setActiveModule] = useState('dashboard');
  const [expandedMenus, setExpandedMenus] = useState({});

  const [showAdminPanel, setShowAdminPanel] = useState(() => {
    const saved = localStorage.getItem('mapasac_admin_panel_open');
    return saved ? JSON.parse(saved) : false;
  });

  const [activeComponent, setActiveComponent] = useState(null);

  // ===========================================
  // ⭐ NUEVO: Restaurar el estado al recargar, bloque de chatgpt verificar
  // ===========================================
  useEffect(() => {
    const saved = localStorage.getItem("mapasac_last_state");
    if (saved) {
      const { activeModule: m, activeComponent: c } = JSON.parse(saved);
      if (m) setActiveModule(m);
      if (c) setActiveComponent(c);
    }
  }, []);
  // ===========================================

  // ===========================================
  // 🔥 TIMER DE SESIÓN (Estilo VFP9 + Bancario)
  // ===========================================
  // Valida token cada 30 segundos en background
  // Si expira: logout automático sin preguntar
  useSessionTimer(onLogout);

  // ===========================================
  // ⭐ NUEVO: Guardar estado al cambiar módulo/form, bloque de chatgpt verificar
  // ===========================================
  useEffect(() => {
    const state = {
      activeModule,
      activeComponent
    };
    localStorage.setItem("mapasac_last_state", JSON.stringify(state));
  }, [activeModule, activeComponent]);
  // ===========================================

  // ===========================================
  // 🧹 LIMPIAR CACHE AL CERRAR/RECARGAR APP
  // ===========================================
  useEffect(() => {
    const handleBeforeUnload = () => cacheClean();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  // ===========================================

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const handleLogout = async () => {
    try {
      cacheClean();
      const token = sessionStorage.getItem('mapasac_access_token');
      if (token) {
        // 🔥 USAR secureFetch para detectar 401
        await secureFetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      sessionStorage.removeItem('mapasac_access_token');
      sessionStorage.removeItem('mapasac_refresh_token');
      sessionStorage.removeItem('mapasac_session');
      toast.success('Sesión cerrada exitosamente');
      onLogout();
    }
  };

  const openConfigPanel = () => {
    setShowAdminPanel(true);
    localStorage.setItem('mapasac_admin_panel_open', 'true');
  };

  return (
    <div className={`flex ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`} style={{ height: 'calc(100vh / var(--ui-escala, 1))' }}>
      {/* Sidebar */}
      {/* Banner de modo DESARROLLO */}
      {isDesarrollo() && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-400 text-black text-center py-1 text-xs font-bold z-50 shadow-lg">
          ⚠️ MODO DESARROLLO - Gráficos UI en Pruebas
        </div>
      )}
      <div className={`w-72 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-900'} text-white flex flex-col shadow-xl`}>
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center mb-2">
            {getDynamicIcon('user-check', 20, 'text-white mr-1')}
            <span className="text-xs text-gray-400">{sessionData?.user?.login}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
            <div className="flex items-center">
              {getDynamicIcon('building-2', 20, 'text-white mr-1')}
              <span className="truncate">{sessionData?.empresa?.nombre}</span>
            </div>
            <div className="flex items-center ml-2">
              {getDynamicIcon('hash', 20, 'text-white mr-1')}
              <span className="text-xs">{sessionData?.database_info?.client_id}</span>
            </div>
          </div>
          <div className="flex items-center mb-2">
            {getDynamicIcon('server', 20, 'text-white mr-1')}
            <span className="text-xs text-gray-400">
              {sessionData?.database_info?.connection_type === 'local' ? 'Local' : 'Nube'}
            </span>
          </div>
          <div className="flex items-center">
            {getDynamicIcon('database', 20, 'text-white mr-1')}
            <span className="text-xs text-gray-400">
              {sessionData?.database_info?.database || 'padm670621'}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <nav className="p-4 space-y-2">
            {sessionData?.rbac?.menu_items?.map((item) => {
              const isExpanded = expandedMenus[item.modulo];
              
              return (
                <div key={item.modulo}>
                  <button
                    onClick={() => {
                      if (item.submenu && item.submenu.length > 0) {
                        toggleMenu(item.modulo);
                      } else {
                        setActiveModule(item.modulo);
                      }
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-700 transition-colors ${
                      activeModule === item.modulo ? 'bg-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      {getDynamicIcon(item.icono || item.icono_modulo || 'circle', 20, 'mr-3')}
                      <span className="text-sm">{item.titulo}</span>
                    </div>
                    {item.submenu && item.submenu.length > 0 && (
                      isExpanded ? 
                        getDynamicIcon('chevron-down', 16) : 
                        getDynamicIcon('chevron-right', 16)
                    )}
                  </button>
                  
                  {isExpanded && item.submenu && (
                    <div className="ml-6 mt-2 space-y-1">
                      {item.submenu.map((subitem) => (
                        <button
                          key={subitem.codigo}
                          onClick={async () => {
                            const permiso = sessionData?.rbac?.permisos?.find(p => p.codigo === subitem.codigo);
  
                            if (permiso) {
                              // 🔥 Validar token ANTES de abrir form
                              const isValid = await verifyTokenNow();
    
                              if (!isValid) {
                                toast.warning('Sesión expirada por seguridad');
                                setTimeout(() => onLogout(), 2000);
                                return;
                              }
    
                              // Token válido → abrir form
                              setActiveComponent(permiso.componente_form);
                            } else {
                              toast.warn("No se encontró permiso para este submódulo");
                            }
  
                            setActiveModule(subitem.codigo);
                          }}
                          className={`w-full flex items-center p-2 rounded-lg text-sm hover:bg-gray-700 transition-colors ${
                            activeModule === subitem.codigo ? 'bg-blue-600' : ''
                          }`}
                        >
                          {getDynamicIcon(subitem.icono || 'circle', 16, 'mr-2 flex-shrink-0')}
                          <span className="text-left leading-tight">{subitem.titulo}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>        
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className={`${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } shadow-sm border-b px-4 py-4`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {sessionData?.rbac?.es_super_admin ? 'MAPASAC, Panel Principal' : 'Dashboard Principal'}
              </h1>
            </div>            
            <div className="flex items-center space-x-4">
              {sessionData?.rbac?.es_el_mero_chicho && (
                <button 
                  onClick={openConfigPanel}
                  className={`p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors`}
                  title="Configuración del Sistema"
                >
                  {getDynamicIcon('settings', 20, isDarkMode ? 'text-white' : 'text-black')}
                </button>
              )}
              
              <span className={`px-3 py-1 ${
                isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
              } rounded-full text-xs font-medium`}>
                {sessionData?.rbac?.roles?.[0]?.nombre || 'Usuario'}
              </span>
              
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
                title="Cambiar tema"
              >
                {isDarkMode ? 
                  getDynamicIcon('sun', 20, 'text-white') : 
                  getDynamicIcon('moon', 20, 'text-black')}
              </button>
              
              <button
                onClick={handleLogout}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-red-600' : 'hover:bg-red-100'
                }`}
                title="Cerrar sesión"
              >
                {getDynamicIcon('log-out', 20, isDarkMode ? 'text-white' : 'text-black')}
              </button>
            </div>
          </div>
        </header>

        <main className={`flex-1 overflow-x-hidden overflow-y-auto ${
          isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
        } p-6`}>
          <div className="max-w-7xl mx-auto">
            {(activeModule === 'dashboard' || !activeModule) && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {sessionData?.rbac?.menu_items?.map((item) => (
                    <div 
                      key={item.modulo} 
                      className={`${
                        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      } rounded-xl shadow-sm border p-6 hover:shadow-md transition-all cursor-pointer`}
                      onClick={() => setActiveModule(item.modulo)}
                    >
                      <div className="flex items-center">
                        <div className={`p-3 rounded-xl ${
                          isDarkMode ? 'bg-blue-900' : 'bg-blue-100'
                        }`}>
                          {getDynamicIcon(item.icono || item.icono_modulo || 'circle', 24, 'text-blue-600')}
                        </div>
                        <div className="ml-4">
                          <h3 className={`text-lg font-semibold ${
                            isDarkMode ? 'text-white' : 'text-gray-800'
                          }`}>{item.titulo}</h3>
                          <p className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>{item.total_permisos || 0} permisos</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Manifiesto del sistema - AL FINAL */}
                <SystemManifesto />
              </div>
            )}            
          </div>
        </main>
      </div>

      {/* MODAL DEL PANEL DE CONFIGURACIÓN */}
      {showAdminPanel && (
        <AdminLayout onClose={() => {
          setShowAdminPanel(false);
          localStorage.removeItem('mapasac_admin_panel_open');
        }} />
      )}

      {/* MODAL UNIVERSAL - Totalmente dinámico */}
      {activeComponent && (
        <SqlexecPanel 
          moduleComponentForm={activeComponent}
          onClose={() => {
            setActiveComponent(null);
            setActiveModule('dashboard');  // ← FORZAR REGRESO AL DASHBOARD
          }}
        />
      )}
    </div>
  );
};

const ToastWrapper = () => {
  const { isDarkMode } = useTheme();
  return (
    <ToastContainer
      position="top-right"
      autoClose={1000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme={isDarkMode ? "dark" : "light"}
      className="z-50"
    />
  );
};

const MapasacERP = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkExistingSession = async () => {
      // ← Delay 3s — cubre gap de arranque
      await new Promise(resolve => setTimeout(resolve, 3000));

      try {
        const savedSession = sessionStorage.getItem('mapasac_session');
        const token = sessionStorage.getItem('mapasac_access_token');

        if (savedSession && token) {
          // 🔥 USAR secureFetch para detectar 401 automáticamente
          const response = await secureFetch('/api/auth/verify', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const session = JSON.parse(savedSession);
            setSessionData(session);
            setIsLoggedIn(true);
          } else {
            sessionStorage.removeItem('mapasac_session');
            sessionStorage.removeItem('mapasac_access_token');
            sessionStorage.removeItem('mapasac_refresh_token');
          }
        }
      } catch (error) {
        console.error('Error verificando sesión:', error);
        // Si es error de sesión expirada, el interceptor ya manejó el redirect
        if (error.message !== 'Session expired') {
          sessionStorage.removeItem('mapasac_session');
          sessionStorage.removeItem('mapasac_access_token');
          sessionStorage.removeItem('mapasac_refresh_token');
        }
      }
      setLoading(false);
    };

    checkExistingSession();
  }, []);

  // ===========================================
  // 🔒 SEGURIDAD: revalidar sesión al restaurar desde bfcache
  // (botón "Atrás"/"Adelante" del navegador)
  // ===========================================
  // checkExistingSession() (arriba) solo corre en un mount real -- cuando el
  // navegador restaura la pagina desde el back-forward cache (bfcache) al
  // presionar "Adelante" tras haber salido con "Atras", React NUNCA vuelve a
  // montar (mismo arbol en memoria, isLoggedIn ya en true) -- por eso el
  // panel aparecia directo sin revalidar nada. No hay React Router/pushState
  // en esta app (navegacion interna es 100% estado de React, nunca toca el
  // historial del navegador) -- cualquier pageshow con persisted:true aqui
  // es, por construccion, "el usuario salio y volvio", nunca navegacion
  // interna legitima. No reemplaza ni toca el listener de beforeunload
  // existente (ese es para cierre de ventana).
  //
  // Confirmado por Martin 2026-09-14 (diseno final, tras probar en real):
  // fuerza logout SIEMPRE que persisted:true, SIN importar si el token
  // sigue vigente -- en un equipo compartido/publico el riesgo no es "el
  // token expiro", es que otra persona use "Adelante" para seguir en la
  // sesion de quien se fue. Por eso ya no se llama a /api/auth/verify (esa
  // version solo cerraba sesion si el token ya era invalido, y en un
  // equipo compartido con sesion de 8h aun vigente no protegia nada real).
  // sessionStorage se limpia AQUI directamente -- el handleLogout() de este
  // componente solo resetea estado de React, no borra los tokens (eso lo
  // hace el handleLogout de DashboardComponent, que no esta en este scope).
  useEffect(() => {
    const handlePageShow = (event) => {
      if (!event.persisted) return;
      if (!isLoggedIn) return;

      sessionStorage.removeItem('mapasac_access_token');
      sessionStorage.removeItem('mapasac_refresh_token');
      sessionStorage.removeItem('mapasac_session');
      handleLogout();
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isLoggedIn]);

  const handleLoginSuccess = (session) => {
    setSessionData(session);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    cacheClean();
    setSessionData(null);
    setIsLoggedIn(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          {getDynamicIcon('loader-2', 48, 'animate-spin mx-auto mb-4 text-blue-600')}
          <p className="text-gray-600 dark:text-gray-400">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      {isLoggedIn ? (
        <SisConfigProvider initialConfig={sessionData?.ui_config}>
          <DashboardComponent 
            sessionData={sessionData} 
            onLogout={handleLogout} 
          />
        </SisConfigProvider>
      ) : (
        <LoginForm onLoginSuccess={handleLoginSuccess} />
      )}      
      <ToastWrapper />
    </ThemeProvider>
  );
};

export default MapasacERP;

//Arbol genealigico del proyecto
//sqlexec/
//├─ panel/
//│  ├─ SqlexecPanel.jsx
//│  ├─ PanelShell.jsx
//│  ├─ PanelHeader.jsx
//│  ├─ PanelTabs.jsx
//│  ├─ TabButton.jsx
//│  ├─ PanelCloseButton.jsx
//│  └─ PanelContent.jsx
//│
//├─ table/
//│  ├─ SqlexecTable.jsx
//│  ├─ TableShell.jsx
//│  ├─ TableHeader.jsx
//│  ├─ TableToolbar.jsx
//│  ├─ TableBody.jsx
//│  ├─ TableFooter.jsx
//│  ├─ TableRow.jsx
//│  ├─ TableCell.jsx
//│  └─ hooks/
//│     ├─ useTableData.js
//│     ├─ useTableSelection.js
//│     ├─ useTableFilters.js
//│     └─ useTablePagination.js
//│
//├─ builder/
//│  ├─ SqlexecBuilder.jsx
//│  ├─ schema/
//│  │  ├─ loadSchema.js
//│  │  ├─ identifyPrimaryKey.js
//│  │  └─ initFormData.js
//│  │
//│  ├─ form/
//│  │  ├─ formStateEngine.js
//│  │  ├─ conditionalDefaults.js
//│  │  ├─ conditionalVisibility.js
//│  │  ├─ conditionalRequired.js
//│  │  └─ touchedControl.js
//│  │
//│  ├─ validation/
//│  │  ├─ validationEngine.js
//│  │  ├─ requiredValidation.js
//│  │  ├─ conditionalValidation.js
//│  │  └─ uniqueAsyncValidation.js
//│  │
//│  ├─ select/
//│  │  ├─ selectEngine.js
//│  │  ├─ independentSelects.js
//│  │  ├─ dependentSelects.js
//│  │  └─ paramChangeDetection.js
//│  │
//│  ├─ concurrency/
//│  │  ├─ optimisticLocking.js
//│  │  └─ conflictResolution.js
//│  │
//│  ├─ crud/
//│  │  ├─ createRecord.js
//│  │  ├─ updateRecord.js
//│  │  ├─ deleteRecord.js
//│  │  └─ reloadFreshData.js
//│  │
//│  └─ ux/
//│     ├─ defensiveMessages.js
//│     ├─ retryStrategy.js
//│     └─ gracefulFallbacks.js
//│
//├─ shared/
//│  ├─ apiClient.js
//│  ├─ debounce.js
//│  ├─ deepClone.js
//│  ├─ safeGet.js
//│  └─ constants.js
//│
//└─ index.js